import { getAuthors } from './author';
import { getLinkedCategories } from './categories';
import { getCommentSection } from './comments';
import { ContentType, MatchState, SportsTickerEventType } from './enums';
import { Article, CommentSection, GalleryArticle, ImageEntry, RessortModuleEntry, SportsLineup, SportsTicker, TeaserEntry } from './interfaces';
import { getEnclosureLink, getExternalLink, getLinkObjectFromTeaser } from './link';
import { getPageInformation } from './pageInformation';
import { getRessortModuleEntry, getTeaserEntry } from './ressort';
import { getContentType } from './types';

export function normalizeTeaserTypes(teaserTypes: string[]) {
  if(!teaserTypes) {
    return []
  }

  const normalizedTeaserTypes: string[] = teaserTypes.map((type) => {
    if(type.indexOf("video") >= 0) {
      return "video"
    } 
    return type
  })

  //and filter duplicates
  return [...new Set(normalizedTeaserTypes)]
}

export function getArticleEntry(entry: any) : Article | GalleryArticle {  

  let type = getContentType(entry)
  let images = entry.images ? (entry.images.map((e) => {
    return getTeaserEntry(e)
  })).filter(e => e != null) : []
  let headerImage: TeaserEntry | ImageEntry | null = null
  if(type != ContentType.Gallery && images?.length > 0) {
    headerImage = images.pop()
    if(headerImage && headerImage.type == ContentType.Image && "image" in headerImage) {
      headerImage = headerImage.image as ImageEntry
    }
  }

  let teasers = entry.objectList ? (entry.objectList.map((e) => {
    return getTeaserEntry(e)
  })).filter(e => e != null) : []

  let articleEntry: Article = {
    id: entry.id,
    type: getContentType(entry),
    date: entry.creationDate ?  Date.parse(entry.creationDate) : Date.now(),
    creationDate: entry.creationDate ?? null,
    title: entry.title ?? null,
    topline: entry.topline ?? null,
    body: entry.body ?? null,
    lead: entry.lead ?? null,
    headerImage: headerImage as ImageEntry,
    images: images,
    status: 200,
    sharingUrl: getExternalLink(entry) ?? null,
    authors: getAuthors(entry),
    location: entry.placemark ?? null,
    pageInformation: getPageInformation(entry),
    paid: entry.paid == true,
    objectListTeasers: teasers,
    objectComments: getCommentSection(entry) as CommentSection,
    showSocialToolbar: entry.hideSocialToolbar != true,
    linkedCategories: getLinkedCategories(entry),
    teaserTypes: normalizeTeaserTypes(entry.teaserTypes ?? []),
    adIdentifier: entry.adIdentifier == true,
    breakingNewsIdentifier: entry.breakingNewsIdentifier == true,
    links: entry.links ?? []
  }

  if(type == ContentType.Gallery) {
    let galleryEntry: GalleryArticle = {
      ...articleEntry,
      voting: entry.voting ?? null
    }
    return galleryEntry
  }

  if(type == ContentType.KickerLiveTicker && entry.kickerLiveticker) {
    const liveTicker = entry.kickerLiveticker
    
    articleEntry.sportsTicker = getSportsTicker(liveTicker) as SportsTicker;
    if(liveTicker.matchDay) {
      articleEntry.sportsMatches = getSportsMatches(liveTicker.matchDay)
    }
    if(liveTicker.tables) {
      articleEntry.sportsTables = getSportsTables(liveTicker.tables)
    }
  }

  if(entry && entry.modules) {
    let modules: RessortModuleEntry[] = []
    if(entry.modules && entry.modules.length > 0) {
      let index = 0
      modules = entry.modules.map((e) => {
       return getRessortModuleEntry(e, index++)
      })
    }

    articleEntry.modules = modules.filter(m => m != null)
  }

  return articleEntry
}

export function getSportsTicker(liveTicker: any) : SportsTicker | undefined {
  const events = liveTicker.events ? liveTicker.events?.map((event) => {
    const minutes = event.timeInfo ?  event.timeInfo?.split("+") : []
    return {
      minute: minutes.length > 0 ? minutes[0].replace("'", "") : "",
      additionalMinute: minutes.length == 2 ? minutes[1] : "",
      headline: event.headline ?? "",
      subline: event.subline ?? "",
      text: event.text ?? "",
      type: getEventType(event.type)
    }
  }) : []

  const lineup: SportsLineup | null = liveTicker.lineUps ? {
    headline: liveTicker.lineUps?.headline ?? "",
    home: {
      team:  getTeam(liveTicker.lineUps?.homeTeam),
      lineUp: liveTicker.lineUps?.homeTeam?.lineUp ?? "",
      coach: liveTicker.lineUps?.homeTeam?.coach ?? "",
      bench: liveTicker.lineUps?.homeTeam?.reserves ?? "",
      substitution: liveTicker.lineUps?.homeTeam?.substitutions ?? []
    },
    away: {
      team: getTeam(liveTicker.lineUps?.awayTeam),
      lineUp: liveTicker.lineUps?.awayTeam?.lineUp ?? "",
      coach: liveTicker.lineUps?.awayTeam?.coach ?? "",
      bench: liveTicker.lineUps?.awayTeam?.reserves ?? "",
      substitution: liveTicker.lineUps?.awayTeam?.substitutions ?? []
    }
  } : null

  return {
    header: {
      tournament: liveTicker.header?.topline ?? "",
      scoreBoardInfo: {
        saison: liveTicker.header?.topline ?? "",
        matchday: "",//"15. Spieltag"
      },
      homeTeam: getTeam(liveTicker.header?.homeTeam, true),
      awayTeam: getTeam(liveTicker.header?.awayTeam, true),
      score: {
        score: liveTicker.header?.score?.currentScore ?? "",
        halfTimeScore: liveTicker.header?.score?.firstScore ?? "",
        state: getScoreState(liveTicker.header?.score  ?? ""),
        subline: liveTicker.header?.score?.subline ?? ""
      },
      goals: liveTicker.header?.goals ? liveTicker.header?.goals?.map((goal) => {
       const minutes = goal.timeInfo ? goal.timeInfo.split("+") : []
      return {
        minute: minutes[0],
        additionalMinute: minutes.length == 2 ? minutes[1] : "",
        headline: goal.currentScore,
        home: goal.homeGoal ? goal.playerName : "",
        away: !goal.homeGoal ? goal.playerName : "",
        type: SportsTickerEventType.Goal
      }
      }) : [],
      matchDate: liveTicker.header?.score?.kickOff ?? ""
    },
    events: events,
    lineup: lineup 
  }
}

function getEventType(type: string) {
  switch (type) {
    case "Besondere Vorkomnisse":
      return SportsTickerEventType.SpecialEvent
      break;
    case "An/Abpfiff":
      return SportsTickerEventType.StartEnd
      break;
    case "Gelbe Karte":
      return SportsTickerEventType.YellowCard
      break;
    case "Archiv - Gelbe Karte":
      return SportsTickerEventType.YellowCard
      break;
    case "Rote Karte":
      return SportsTickerEventType.RedCard
      break;
    case "Gelb-Rot (Trainer)":
      return SportsTickerEventType.YellowRedCard
      break;
    case "Gelb-Rote Karte":
      return SportsTickerEventType.YellowRedCard
      break;
    case "Gelb (Trainer)":
      return SportsTickerEventType.YellowCard
      break;
    case "An/Abpfiff":
      return SportsTickerEventType.StartEnd
      break;
    case "Tor":
      return SportsTickerEventType.Goal
      break;
    case "Einwechslung":
      return SportsTickerEventType.Substitution
      break;
    case "Elfmeter gepfiffen":
      return SportsTickerEventType.Penalty
      break;
    case "Elfmeterschießen":
      return SportsTickerEventType.Penalty
      break;
    case "Videobeweis - Entscheidung":
      return SportsTickerEventType.VideoAssist
      break;
    case "Videobeweis - Prüfung":
      return SportsTickerEventType.VideoAssist
      break;
    case "advert":
      return SportsTickerEventType.Ad
      break;
    default:
      console.log("unknown event Type: " + type)
      return SportsTickerEventType.Unknown
      break;
  }
}

export function getSportsMatches(entry: any) {
  return {
    headline: entry.headline ?? "",
    topline: entry.topline ?? "",
    matches: entry.matches ? entry.matches?.map((match) => {
      const linkObject = getLinkObjectFromTeaser(match)
      return {
        id: linkObject?.internalLink ?? "",
        articleId: linkObject?.articleId ?? "",
        homeTeam: getTeam(match.homeTeam),
        awayTeam: getTeam( match.awayTeam),
        score: {
          score: match?.score?.currentScore ?? "",
          halfTimeScore: match?.score?.firstScore ?? "",
          state: getScoreState(match?.score),
          subline: match?.score?.subline ?? ""
        },
        matchDate: match?.score?.kickOff ?? ""
      }
    }) : []
  }
}

export function getSportsTables(entries: any) {
  const tables = entries?.map((table) => {
    //console.log("headline", table.headline)
    return {
      headline: table.headline,
      entries: table.teams?.map((team) => {
      return {
        position: team.placement,
        matches: team.matchCount,
        points: team.points,
        goalDifference: team.goalDifference,
        team: getTeam(team)
      }
      })
    }
  })
  return tables ? tables : []
}

function getTeam(team: any, largeIcon?: boolean) {
  let unscaledUrl = team?.images?.[0] ? getEnclosureLink(team?.images?.[0]) : ""
  return {
    name: team?.name ?? "",
    icon: unscaledUrl ? unscaledUrl + (largeIcon ? "&width=152" : "&width=36") : ""
  }
}

function getScoreState(score: any) {
  if(score.isFinished) {
    return MatchState.Finished
  }
  if(score.kickOff) {
    return MatchState.Pre
  }
  return MatchState.Live
}

export function getGalleryImageCountText(count: number): string {
  return count === 1 ? "1 Bild" : `${count} Bilder`
}

